import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { CHAIN_LABELS, buildDexScreenerUrl, buildFomoUrl, formatTime } from '../utils/masterChefUtils';

function formatMC(mc) {
  const n = Number(mc) || 0;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
}

function formatPrice(p) {
  const n = Number(p) || 0;
  if (n === 0) return '—';
  if (n < 0.00001) return `$${n.toExponential(2)}`;
  return `$${n.toPrecision(4)}`;
}

function pct(v, digits = 0) {
  if (v == null || Number.isNaN(v)) return '—';
  const sign = v > 0 ? '+' : '';
  return `${sign}${Number(v).toFixed(digits)}%`;
}

const STATUS_BADGE = {
  waiting: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
  entered: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
  expired: 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400',
};

export default function MasterChefStaged() {
  const [signals, setSignals] = useState([]);
  const [pullback, setPullback] = useState({});
  const [statusFilter, setStatusFilter] = useState('waiting');
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/masterchef/staged-recoveries', { params: { status: statusFilter } });
      setSignals(res.data.signals || []);
      setPullback(res.data.pullback || {});
    } catch (err) {
      console.error('Failed to fetch staged recoveries:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    setLoading(true);
    fetchData();
    const interval = setInterval(fetchData, 20000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const waitingCount = signals.filter((s) => s.status === 'waiting').length;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Staged Recovering</h2>
          <p className="text-xs text-gray-500">
            Tokens that jumped past the recovery entry window — watched for a pullback from peak before buying.
            Includes both the house engine and per-user strategy pullbacks (see Strategy column).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-xs px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="waiting">Watching</option>
            <option value="entered">Entered</option>
            <option value="expired">Expired</option>
            <option value="all">All</option>
          </select>
        </div>
      </div>

      {/* Config banner */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Pullback mode</h3>
          <span className={`text-xs px-2 py-0.5 rounded-full ${
            pullback.enabled
              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
          }`}>
            {pullback.enabled ? 'Enabled' : 'Disabled'}
          </span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-3 text-xs">
          <div>
            <p className="text-gray-400">Watching</p>
            <p className="text-gray-900 dark:text-white font-semibold text-base">{waitingCount}</p>
          </div>
          <div>
            <p className="text-gray-400">Stage from</p>
            <p className="text-gray-900 dark:text-white font-semibold text-base">{pct(pullback.minRecoveryPercent)}</p>
          </div>
          <div>
            <p className="text-gray-400">Stage up to</p>
            <p className="text-gray-900 dark:text-white font-semibold text-base">{pct(pullback.maxRecoveryPercent)}</p>
          </div>
          <div>
            <p className="text-gray-400">Buy on dip</p>
            <p className="text-gray-900 dark:text-white font-semibold text-base">-{pullback.pullbackPercent ?? 20}%</p>
          </div>
          <div>
            <p className="text-gray-400">Min MC</p>
            <p className="text-gray-900 dark:text-white font-semibold text-base">{formatMC(pullback.minMarketCap)}</p>
          </div>
          <div>
            <p className="text-gray-400">Max MC</p>
            <p className="text-gray-900 dark:text-white font-semibold text-base">{formatMC(pullback.maxMarketCap)}</p>
          </div>
        </div>
        {!pullback.enabled && (
          <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-3">
            House-engine pullback mode is off (settings above apply to the house engine only). Per-user recovery
            strategies stage independently — their pullbacks still appear below with the owning strategy.
          </p>
        )}
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading staged tokens…</div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          {signals.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-400">
              No tokens staged. When a recovery jumps past the entry window, it shows up here.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] text-gray-500 dark:text-gray-400 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                    <th className="text-left px-4 py-2">Token</th>
                    <th className="text-left px-2 py-2">Strategy</th>
                    <th className="text-left px-2 py-2">Chain</th>
                    <th className="text-right px-2 py-2">Recovery @ stage</th>
                    <th className="text-right px-2 py-2">Peak gain</th>
                    <th className="text-right px-2 py-2">From peak</th>
                    <th className="text-right px-2 py-2">To trigger</th>
                    <th className="text-right px-2 py-2">Last / Target</th>
                    <th className="text-right px-2 py-2">MC</th>
                    <th className="text-left px-2 py-2">Status</th>
                    <th className="text-left px-2 py-2">Checked</th>
                    <th className="text-right px-4 py-2">Links</th>
                  </tr>
                </thead>
                <tbody>
                  {signals.map((s) => {
                    const armed = s.distanceToTargetPercent != null && s.distanceToTargetPercent <= 0;
                    return (
                      <tr key={s._id} className="border-b dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30">
                        <td className="px-4 py-2">
                          <a
                            href={buildDexScreenerUrl(s.chain, s.tokenAddress)}
                            target="_blank"
                            rel="noreferrer"
                            className="font-medium text-gray-900 dark:text-white hover:text-blue-500"
                          >
                            {s.tokenSymbol || 'TOKEN'}
                          </a>
                          <p className="text-[10px] text-gray-400 font-mono truncate max-w-[140px]">{s.tokenAddress}</p>
                        </td>
                        <td className="px-2 py-2 text-xs">
                          <span className={`${s.strategyName === 'House' ? 'text-gray-500' : 'text-blue-600 dark:text-blue-400'}`}>{s.strategyName || 'House'}</span>
                          {s.owner && <p className="text-[10px] text-gray-400 font-mono">{s.owner}</p>}
                        </td>
                        <td className="px-2 py-2 text-xs">{CHAIN_LABELS[s.chain] || s.chain}</td>
                        <td className="px-2 py-2 text-right text-xs text-gray-700 dark:text-gray-300">{pct(s.stagedRecoveryPercent)}</td>
                        <td className="px-2 py-2 text-right text-xs text-green-600 dark:text-green-400">{pct(s.peakGainPercent)}</td>
                        <td className="px-2 py-2 text-right text-xs text-red-500">{pct(s.downFromPeakPercent, 1)}</td>
                        <td className={`px-2 py-2 text-right text-xs font-medium ${armed ? 'text-green-600 dark:text-green-400' : 'text-gray-700 dark:text-gray-300'}`}>
                          {armed ? 'READY' : pct(s.distanceToTargetPercent, 1)}
                        </td>
                        <td className="px-2 py-2 text-right text-[11px] text-gray-500">
                          <span className="text-gray-900 dark:text-white">{formatPrice(s.lastPrice)}</span>
                          <span className="text-gray-400"> / {formatPrice(s.targetEntryPrice)}</span>
                        </td>
                        <td className="px-2 py-2 text-right text-xs text-gray-500">{formatMC(s.lastMarketCap || s.stagedMarketCap)}</td>
                        <td className="px-2 py-2">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${STATUS_BADGE[s.status] || ''}`}>
                            {s.status}
                          </span>
                        </td>
                        <td className="px-2 py-2 text-xs text-gray-500">{s.lastCheckedAt ? formatTime(s.lastCheckedAt) : '—'}</td>
                        <td className="px-4 py-2 text-right whitespace-nowrap">
                          <a href={buildDexScreenerUrl(s.chain, s.tokenAddress)} target="_blank" rel="noreferrer" className="text-xs text-blue-500 hover:underline mr-2">📊</a>
                          {buildFomoUrl(s.chain, s.tokenAddress) && (
                            <a href={buildFomoUrl(s.chain, s.tokenAddress)} target="_blank" rel="noreferrer" className="text-xs text-orange-500 hover:underline">🔥</a>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
